import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import mitt from 'mitt';
var animationId = null;
var emitter = mitt();

var handleMeasure = function handleMeasure() {
  if (animationId === null) {
    animationId = window.requestAnimationFrame(function () {
      emitter.emit('measure');
      window.requestAnimationFrame(function () {
        animationId = null;
      });
    });
  }
};

var mutationObserver = typeof window === 'undefined' ? {} : new MutationObserver(handleMeasure);
var resizeObserver = typeof window === 'undefined' ? {} : new ResizeObserver(handleMeasure);
/**
 * Creates a set of hooks to help manage collecting and using measurements
 * from one or many components.
 *
 * @example
 *
 * function Singular({ ref, value }) {
 *   const [useMeasureRef, useMeasureEffect] = useMeasure()
 *   useMeasureRef(ref)
 *   useMeasureEffect(([node]) => {
 *     console.log(node)
 *   }, [value])
 *   return <div />
 * }
 *
 * function Parent({ children }) {
 *   const [useMeasureRef, useMeasureEffect] = useMeasure()
 *   useMeasureEffect(nodes => {
 *     console.log(nodes)
 *   }, [])
 *   return (
 *     <div>
 *       {Children.map(children, child =>
 *         React.cloneElement(child, { useMeasureRef })
 *       )}
 *     </div>
 *   )
 * }
 *
 * function Child({ useMeasureRef }) {
 *   const ref = useMeasureRef(ref)
 *   return <div ref={ref} />
 * }
 *
 * @returns {[(ref?: React.RefObject) => React.RefObject, (onMeasure: Function, dependencies: Array) => void]}
 */

export function useMeasure() {
  var refs = React.useRef([]);
  var storeRef = React.useCallback(function (node) {
    if (refs.current.indexOf(node) === -1) {
      refs.current.push(node);
    }
  }, []);
  var refsEmitter = React.useMemo(function () {
    var emitter = mitt();
    emitter.on('storeRef', storeRef);
    return emitter;
  }, []);
  var useMeasureRef = React.useCallback(function (ref) {
    var measureRef = ref || React.useRef();
    var connected = React.useRef(false);
    React.useLayoutEffect(function () {
      var node = measureRef.current;
      mutationObserver.observe(node, {
        characterData: true,
        childList: true,
        subtree: true
      });
      resizeObserver.observe(node);
      return function () {
        resizeObserver.unobserve(node);
      };
    }, []);
    React.useLayoutEffect(function () {
      refsEmitter.emit('storeRef', measureRef.current);
    });
    return measureRef;
  }, []);
  var useMeasureEffect = React.useCallback(function (onMeasure, dependencies) {
    React.useLayoutEffect(function () {
      function handleMeasure() {
        onMeasure([].concat(refs.current));
      }

      handleMeasure();
      emitter.on('measure', handleMeasure);
      return function () {
        emitter.off('measure', handleMeasure);
      };
    }, dependencies);
    React.useLayoutEffect(function () {
      return function () {
        refs.current = [];
      };
    });
    React.useEffect(function () {
      return function () {
        refsEmitter.off('storeRef', storeRef);
      };
    }, []);
  }, []);
  return [useMeasureRef, useMeasureEffect];
}